/**
 * SmartUploadDialog — wraps UploadDialog + post-upload compatibility check + CompatibilityDialog.
 *
 * After a successful upload, it automatically checks for an approved recipe.
 * If one exists, it shows the CompatibilityDialog so the user can choose what to do.
 * This eliminates the need for each page to duplicate compatibility-check logic.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  LinearProgress,
  Typography,
} from '@mui/material';
import { Schema } from '../../services/api';
import * as mappingApi from '../../services/mappingApi';
import type {
  CompatibilityVerdict,
  ProjectRecipeSummary,
  ExecutionRun,
} from '../../services/types';
import NewTransformationWizard from './NewTransformationWizard';
import * as projectApi from '../../services/projectApi';
import CompatibilityDialog from './CompatibilityDialog';
import RecipePickerDialog from './RecipePickerDialog';
import { useNotifications } from '../../context/NotificationContext';

export type CompatibilityAction =
  | 'none'
  | 'apply_and_load'
  | 'review_first'
  | 'regenerate_delta'
  | 'regenerate_full'
  | 'cancel';

export interface UploadCompletionResult {
  batchId: string;
  status: string;
  files?: string[];
  filename?: string;
  action: CompatibilityAction;
  executionResult?: ExecutionRun;
  recipe?: ProjectRecipeSummary;
  verdict?: CompatibilityVerdict;
}

interface SmartUploadDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string | null;
  schemaSet: Schema | null | undefined;
  title?: string;
  defaultSourceConnectorId?: string | null;
  /** Called after the full flow completes (upload + optional compatibility action). */
  onComplete: (result: UploadCompletionResult) => void;
}

type Phase = 'upload' | 'checking' | 'compatibility' | 'executing' | 'pick_baseline';

const SmartUploadDialog: React.FC<SmartUploadDialogProps> = ({
  open,
  onClose,
  projectId,
  schemaSet,
  title,
  defaultSourceConnectorId,
  onComplete,
}) => {
  const { latestToast, dismissToast } = useNotifications();
  const [phase, setPhase] = useState<Phase>('upload');
  const [busy, setBusy] = useState(false);
  const [batchResult, setBatchResult] = useState<{
    batch_id: string;
    status: string;
    files?: string[];
    filename?: string;
  } | null>(null);
  const [recipe, setRecipe] = useState<ProjectRecipeSummary | null>(null);
  const [verdict, setVerdict] = useState<CompatibilityVerdict | null>(null);
  const [baselineSource, setBaselineSource] = useState<'own' | 'cross_project' | null>(null);

  // Suppress SSE toasts (e.g. "Profiling Complete") for the same batch while the
  // CompatibilityDialog or execution overlay is active — showing a success toast
  // alongside an "Incompatible Schema" error is confusing.
  useEffect(() => {
    if (!latestToast || !batchResult) return;
    if (phase !== 'compatibility' && phase !== 'executing' && phase !== 'checking') return;
    if (latestToast.entity_type === 'batch' && latestToast.entity_id === batchResult.batch_id) {
      dismissToast();
    }
  }, [latestToast, batchResult, phase, dismissToast]);

  const reset = useCallback(() => {
    setPhase('upload');
    setBusy(false);
    setBatchResult(null);
    setRecipe(null);
    setVerdict(null);
    setBaselineSource(null);
  }, []);

  const handleClose = useCallback(() => {
    if (phase === 'executing') return; // Don't close during execution
    reset();
    onClose();
  }, [phase, reset, onClose]);

  const completeWith = useCallback(
    (action: CompatibilityAction, executionResult?: ExecutionRun) => {
      if (!batchResult) return;
      const result: UploadCompletionResult = {
        batchId: batchResult.batch_id,
        status: batchResult.status,
        files: batchResult.files,
        filename: batchResult.filename,
        action,
        executionResult,
        recipe: recipe ?? undefined,
        verdict: verdict ?? undefined,
      };
      reset();
      onComplete(result);
    },
    [batchResult, recipe, verdict, reset, onComplete],
  );

  /** Called when NewTransformationWizard finishes submitting sources.
   *  The wizard creates fresh transformations — skip recipe compatibility
   *  checks (those belong to the "Reuse Pipeline" flow, not new uploads).
   */
  const handleUploadComplete = useCallback(
    (batch: { batch_id: string; status: string; files?: string[]; filename?: string }) => {
      setBatchResult(batch);
      reset();
      onComplete({
        batchId: batch.batch_id,
        status: batch.status,
        files: batch.files,
        filename: batch.filename,
        action: 'none',
      });
    },
    [reset, onComplete],
  );

  /** Apply & Load: execute recipe against the new batch. */
  const handleApplyAndLoad = useCallback(async () => {
    if (!recipe || !batchResult || busy) return;
    setBusy(true);
    setPhase('executing');
    try {
      const result = await mappingApi.applyRecipe(recipe.recipe_id, batchResult.batch_id);
      if (result.action === 'direct_execution' && result.execution_result) {
        completeWith('apply_and_load', result.execution_result);
      } else {
        // Unexpected response — treat as normal completion
        completeWith('apply_and_load');
      }
    } catch (error) {
      console.error('Apply & Load failed:', error);
      // Fall back — let user navigate to batch
      completeWith('apply_and_load');
    }
  }, [recipe, batchResult, completeWith]);

  const handleReviewFirst = useCallback(() => {
    if (busy) return;
    completeWith('review_first');
  }, [busy, completeWith]);

  const handleRegenerateDelta = useCallback(() => {
    if (busy) return;
    completeWith('regenerate_delta');
  }, [busy, completeWith]);

  const handleRegenerateFull = useCallback(() => {
    if (busy) return;
    completeWith('regenerate_full');
  }, [busy, completeWith]);

  /** Cancel: delete the batch and return to previous screen. */
  const handleCancel = useCallback(async () => {
    if (!batchResult || busy) return;
    setBusy(true);
    try {
      await mappingApi.deleteBatch(batchResult.batch_id);
      completeWith('cancel');
    } catch (error) {
      console.error('Failed to delete cancelled batch:', error);
      // Delete failed — batch still exists, fall through to 'none' so user can find it
      completeWith('none');
    }
  }, [batchResult, busy, completeWith]);

  /** Open RecipePickerDialog to change baseline. */
  const handleChangeBaseline = useCallback(() => {
    setPhase('pick_baseline');
  }, []);

  /** Called when user selects an external recipe from RecipePickerDialog. */
  const handleSelectExternalRecipe = useCallback(
    async (selectedRecipe: ProjectRecipeSummary) => {
      if (!batchResult) return;
      setRecipe(selectedRecipe);
      setBaselineSource('cross_project');
      setPhase('checking');
      try {
        const compat = await mappingApi.checkSchemaCompatibility(
          selectedRecipe.recipe_id,
          batchResult.batch_id,
        );
        setVerdict(compat);
        setPhase('compatibility');
      } catch (error) {
        console.error('Cross-project compatibility check failed:', error);
        setRecipe(null);
        setBaselineSource(null);
        setPhase('compatibility'); // Show previous state
      }
    },
    [batchResult],
  );

  // --- Render ---

  // Phase: upload — show the normal UploadDialog
  if (phase === 'upload') {
    return (
      <NewTransformationWizard
        open={open}
        onClose={handleClose}
        onUploadComplete={handleUploadComplete}
        projectId={projectId}
        schemaSet={schemaSet}
        title={title}
        defaultSourceConnectorId={defaultSourceConnectorId}
      />
    );
  }

  // Phase: checking — show a small loading dialog with cancel option
  if (phase === 'checking') {
    return (
      <Dialog open maxWidth="xs" fullWidth>
        <DialogContent>
          <Box display="flex" alignItems="center" gap={2} py={2}>
            <CircularProgress size={24} />
            <Box>
              <Typography variant="body1">Checking recipe compatibility...</Typography>
              <Button
                size="small"
                color="inherit"
                sx={{ mt: 0.5, textTransform: 'none' }}
                onClick={() => {
                  // Skip compatibility check — proceed as normal upload
                  if (batchResult) {
                    reset();
                    onComplete({
                      batchId: batchResult.batch_id,
                      status: batchResult.status,
                      files: batchResult.files,
                      filename: batchResult.filename,
                      action: 'none',
                    });
                  }
                }}
              >
                Skip check
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Phase: pick_baseline — show RecipePickerDialog
  if (phase === 'pick_baseline' && projectId) {
    return (
      <RecipePickerDialog
        open
        onClose={() => setPhase('compatibility')}
        onSelect={(selected) => handleSelectExternalRecipe(selected)}
        currentProjectId={projectId}
        currentTargetSchemaId={schemaSet?.schema_id}
      />
    );
  }

  // Phase: compatibility — show the CompatibilityDialog
  if (phase === 'compatibility' && batchResult) {
    return (
      <CompatibilityDialog
        open
        verdict={verdict}
        recipe={recipe}
        batchId={batchResult.batch_id}
        onApplyAndLoad={handleApplyAndLoad}
        onReviewFirst={handleReviewFirst}
        onRegenerateDelta={handleRegenerateDelta}
        onRegenerateFull={handleRegenerateFull}
        onCancel={handleCancel}
        busy={busy}
        baselineSource={baselineSource}
        sourceProjectName={recipe?.project_name}
        onChangeBaseline={handleChangeBaseline}
      />
    );
  }

  // Phase: executing — show loading overlay
  if (phase === 'executing') {
    return (
      <Dialog open maxWidth="xs" fullWidth>
        <DialogContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <CircularProgress size={24} />
            <Typography variant="h6">Loading Data...</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Applying approved recipe to your data. This may take a moment.
          </Typography>
          <LinearProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return null;
};

export default SmartUploadDialog;
